import { v4 as uuidv4 } from 'uuid';
import { createSlice } from '@reduxjs/toolkit';
import { logout } from './auth-slice';

const cartSlice = createSlice({
    name: "cart",
    initialState: {
        cartItems: [],
        validationWarnings: [],
    },
    reducers: {
        addToCart(state, action) {
            const product = action.payload;
            if(!product.variation){
                const cartItem = state.cartItems.find(item => item.code === product.code);
                if(!cartItem){
                    state.cartItems.push({
                        ...product,
                        quantity: product.quantity ? product.quantity : 1,
                        observaciones: product.observaciones ?? '',
                        cartItemId: uuidv4()
                    });
                } else {
                    state.cartItems = state.cartItems.map(item => {
                        if(item.cartItemId === cartItem.cartItemId){
                            return {
                                ...item,
                                quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1
                            }
                        }
                        return item;
                    })
                }

            } else {
                const cartItem = state.cartItems.find(
                    item =>
                        item.code === product.code &&
                        product.selectedProductColor &&
                        product.selectedProductColor === item.selectedProductColor &&
                        product.selectedProductSize &&
                        product.selectedProductSize === item.selectedProductSize &&
                        (product.cartItemId ? product.cartItemId === item.cartItemId : true)
                );
                if(!cartItem){
                    state.cartItems.push({
                        ...product,
                        quantity: product.quantity ? product.quantity : 1,
                        observaciones: product.observaciones ?? '',
                        cartItemId: uuidv4()
                    });
                } else if (cartItem !== undefined && (cartItem.selectedProductColor !== product.selectedProductColor || cartItem.selectedProductSize !== product.selectedProductSize)) {
                    state.cartItems = [
                        ...state.cartItems,
                        {
                            ...product,
                            quantity: product.quantity ? product.quantity : 1,
                            observaciones: product.observaciones ?? '',
                            cartItemId: uuidv4()
                        }
                    ]
                } else {
                    state.cartItems = state.cartItems.map(item => {
                        if(item.cartItemId === cartItem.cartItemId){
                            return {
                                ...item,
                                quantity: product.quantity ? item.quantity + product.quantity : item.quantity + 1,
                                selectedProductColor: product.selectedProductColor,
                                selectedProductSize: product.selectedProductSize
                            }
                        }
                        return item;
                    });
                }
            }

        },
        clearCart(state, action) {
            state.cartItems = [];
        },
        deleteFromCart(state, action) {
            state.cartItems = state.cartItems.filter(item => item.cartItemId !== action.payload);
        },
        decreaseQuantity(state, action){
            const product = action.payload;
            if (product.quantity === 1) {
                state.cartItems = state.cartItems.filter(item => item.cartItemId !== product.cartItemId);
            } else {
                state.cartItems = state.cartItems.map(item =>
                    item.cartItemId === product.cartItemId
                        ? { ...item, quantity: item.quantity - 1 }
                        : item
                );
            }
        },
        deleteAllFromCart(state){
            state.cartItems = [];
        },
        clearValidationWarnings(state) {
            state.validationWarnings = [];
        },
        applyValidation(state, action) {
            const { items, warnings } = action.payload;
            const validatedMap = {};
            for (const item of items) {
                validatedMap[item.product_code] = item;
            }
            // Warnings bloqueantes: en vez de borrar el item del carrito
            // (lo que lo hacía desaparecer sin dejar rastro), lo reclasifica
            // a "guardado para más tarde" con el mismo motivo que usa el
            // servidor — el próximo GET /carrito lo persiste igual, esto es
            // solo para no mostrar datos stale en la ventana entre fetches.
            const motivoByWarningType = {
                discontinued: "discontinued",
                out_of_stock: "sin_stock",
                product_not_found: "no_encontrado",
            };
            const blockedMotivoByCode = {};
            for (const w of warnings ?? []) {
                const motivo = motivoByWarningType[w.type];
                if (motivo) {
                    blockedMotivoByCode[w.product_code] = motivo;
                }
            }

            state.cartItems = state.cartItems.map(cartItem => {
                const blockedMotivo = blockedMotivoByCode[cartItem.code];
                if (blockedMotivo) {
                    return {
                        ...cartItem,
                        guardado_para_despues: true,
                        guardado_motivo: blockedMotivo,
                    };
                }

                const validated = validatedMap[cartItem.code];
                if (!validated) {
                    return cartItem;
                }

                return {
                    ...cartItem,
                    quantity: validated.quantity,
                    _validated_price: validated.final_unit_price,
                    _promo: validated.promo,
                    _promo_discount_pct: validated.promo_discount_pct,
                    _bulto_split: validated.bulto_split ?? null,
                };
            });
            state.validationWarnings = warnings ?? [];
        },
        setCartFromApi(state, action) {
            // action.payload = array de items del formato API
            state.cartItems = action.payload.map(item => ({
                ...item.product,
                quantity: item.quantity,
                observaciones: item.observaciones ?? '',
                cartItemId: `api-${item.carrito_item_id}`,
                _api_item_id: item.carrito_item_id,
                guardado_para_despues: item.guardado_para_despues ?? false,
                guardado_motivo: item.guardado_motivo ?? null,
            }));
        },
        attachApiId(state, action) {
            // Vincula el _api_item_id que devuelve el POST /carrito/items al item
            // recién agregado, SIN cambiar su cartItemId. Mantener el cartItemId
            // estable evita dejar huérfano el debounce que el usuario pueda tener
            // en curso (ver bug "queda en 1"). Reemplaza al viejo fetchCart().
            const { cartItemId, apiItemId, quantity } = action.payload;
            state.cartItems = state.cartItems.map(item =>
                item.cartItemId === cartItemId
                    ? {
                        ...item,
                        _api_item_id: apiItemId,
                        ...(quantity != null ? { quantity } : {}),
                    }
                    : item
            );
        },
        setQuantity(state, action) {
            const { cartItemId, quantity } = action.payload;
            state.cartItems = state.cartItems.map(item =>
                item.cartItemId === cartItemId
                    ? { ...item, quantity, _validated_price: undefined, _promo: undefined, _promo_discount_pct: undefined, _bulto_split: undefined }
                    : item
            );
            state.validationWarnings = [];
        },
        updateItemObservaciones(state, action) {
            const { cartItemId, observaciones } = action.payload;
            state.cartItems = state.cartItems.map(item =>
                item.cartItemId === cartItemId
                    ? { ...item, observaciones }
                    : item
            );
        },
        setGuardadoParaDespues(state, action) {
            const { cartItemId, value, motivo } = action.payload;
            state.cartItems = state.cartItems.map(item =>
                item.cartItemId === cartItemId
                    ? { ...item, guardado_para_despues: value, guardado_motivo: value ? (motivo ?? 'manual') : null }
                    : item
            );
        },
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => {
            state.cartItems = [];
            state.validationWarnings = [];
        });
    },
});

export const { addToCart, deleteFromCart, decreaseQuantity, deleteAllFromCart, clearCart, setCartFromApi, applyValidation, clearValidationWarnings, setQuantity, updateItemObservaciones, attachApiId, setGuardadoParaDespues } = cartSlice.actions;
export default cartSlice.reducer;
